<template>
  <div class="material-summary-page">
    <div class="page-header">
      <div class="header-content">
        <button type="button" class="back-button" @click="goBack">
          <span class="back-icon">←</span>
          <span>返回数据概览</span>
        </button>
        <div class="title-block">
          <div class="title-icon">🎞️</div>
          <div>
            <h1>素材汇总</h1>
            <p>按 target_name 查看素材消耗、净 ROI 与视频预览</p>
          </div>
        </div>
      </div>
    </div>

    <div class="page-content">
      <div class="toolbar-card">
        <div class="toolbar-left">
          <div class="toolbar-title">素材扫描结果</div>
          <div class="toolbar-subtitle">共 {{ filteredGroups.length }} 个 target_name 分组，{{ totalVideoCount }} 个视频素材</div>
        </div>
        <div class="toolbar-right">
          <label class="toolbar-label toolbar-date-range">
            <span>开始日期</span>
            <input
              v-model="startDate"
              type="date"
              class="toolbar-input toolbar-date-input"
              :disabled="loading || batchDownloading"
            />
          </label>
          <label class="toolbar-label toolbar-date-range">
            <span>结束日期</span>
            <input
              v-model="endDate"
              type="date"
              class="toolbar-input toolbar-date-input"
              :disabled="loading || batchDownloading"
            />
          </label>
          <label class="toolbar-label toolbar-threshold">
            <span>总消耗阈值</span>
            <input
              v-model.number="statCostThreshold"
              type="number"
              min="0"
              step="0.01"
              class="toolbar-input"
              :disabled="loading || batchDownloading"
            />
          </label>
          <label class="toolbar-label toolbar-threshold">
            <span>净 ROI 阈值</span>
            <input
              v-model.number="roiThreshold"
              type="number"
              min="0"
              step="0.01"
              class="toolbar-input"
              :disabled="loading || batchDownloading"
            />
          </label>
          <label class="toolbar-label">
            <span>搜索分组</span>
            <input
              v-model.trim="keyword"
              type="text"
              class="toolbar-input"
              placeholder="输入 target_name"
              :disabled="loading || batchDownloading"
            />
          </label>
          <button class="ghost-button" type="button" :disabled="loading" @click="fetchSummary">
            {{ loading ? '查询中...' : '查询' }}
          </button>
          <button
            class="ghost-button"
            type="button"
            :disabled="batchDownloading || totalVideoCount === 0"
            @click="downloadAllVideos"
          >
            {{ batchDownloading ? `下载中 ${downloadProgressText}` : `批量下载全部 (${totalVideoCount})` }}
          </button>
          <button
            class="primary-button"
            type="button"
            :disabled="batchDownloading || selectedVideos.length === 0"
            @click="downloadSelectedVideos"
          >
            {{ batchDownloading ? `下载中 ${downloadProgressText}` : `批量下载已选 (${selectedVideos.length})` }}
          </button>
        </div>
      </div>

      <div class="summary-grid">
        <div class="summary-card">
          <div class="summary-label">分组数量</div>
          <div class="summary-value">{{ filteredGroups.length }}</div>
        </div>
        <div class="summary-card">
          <div class="summary-label">素材卡片</div>
          <div class="summary-value">{{ totalVideoCount }}</div>
        </div>
        <div class="summary-card">
          <div class="summary-label">视频素材</div>
          <div class="summary-value">{{ totalVideoCount }}</div>
        </div>
        <div class="summary-card">
          <div class="summary-label">已选视频</div>
          <div class="summary-value">{{ selectedVideos.length }}</div>
        </div>
      </div>

      <div v-if="statusMessage" class="status-banner" :class="statusType">
        {{ statusMessage }}
      </div>

      <div v-if="batchDownloading" class="progress-card">
        <div class="progress-head">
          <span>批量下载进度</span>
          <span>{{ downloadProgressText }}</span>
        </div>
        <div class="progress-bar">
          <div class="progress-bar-inner" :style="{ width: downloadProgressPercent + '%' }"></div>
        </div>
      </div>

      <div v-if="!loading && filteredGroups.length === 0" class="empty-card">
        {{ groups.length === 0 ? '暂无素材汇总数据' : '当前搜索条件下没有匹配结果' }}
      </div>

      <div v-for="group in filteredGroups" :key="group.groupKey" class="target-card">
        <div class="target-card-header">
          <div class="target-base">
            <div class="target-title-row">
              <h2 class="target-name">{{ group.targetName || '-' }}</h2>
              <span class="target-chip soft">target_name</span>
            </div>
            <div class="target-meta">
              <span>素材数量 {{ group.materialCount }}</span>
            </div>
          </div>
          <div class="target-actions">
            <button class="ghost-button" type="button" :disabled="batchDownloading || group.materialCount === 0" @click="toggleTargetSelection(group)">
              {{ isTargetFullySelected(group) ? '取消本组全选' : '本组全选' }}
            </button>
            <button class="primary-button" type="button" :disabled="batchDownloading || group.materialCount === 0" @click="downloadGroupVideos(group)">
              下载本分组视频
            </button>
          </div>
        </div>

        <div v-if="group.materials.length === 0" class="group-empty-card">
          当前账户下暂无符合条件的素材
        </div>

        <div class="video-grid">
          <div v-for="video in group.materials" :key="video.videoKey" class="video-card">
              <label class="video-select">
                <input
                  type="checkbox"
                  :checked="isVideoSelected(video.videoKey)"
                  :disabled="batchDownloading"
                  @change="toggleVideoSelection(video.videoKey)"
                />
                <span>选择</span>
              </label>
              <video
                class="video-preview"
                :src="video.url"
                controls
                preload="metadata"
                playsinline
              >
                当前浏览器不支持视频预览
              </video>
              <div class="video-info">
                <div class="video-info-row">
                  <span class="video-label">素材 ID</span>
                  <span class="video-value">{{ video.materialId }}</span>
                </div>
                <div class="video-info-row">
                  <span class="video-label">总消耗</span>
                  <span class="video-value">{{ formatNumber(video.statCostForRoi2) }}</span>
                </div>
                <div class="video-info-row">
                  <span class="video-label">净 ROI</span>
                  <span class="video-value">{{ formatNumber(video.totalPrepayAndPaySettleRoi21h) }}</span>
                </div>
              </div>
              <div class="video-actions">
                <button class="primary-button" type="button" :disabled="batchDownloading || !video.url" @click="downloadSingleVideo(video)">
                  单个下载
                </button>
              </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import API_CONFIG from '@/api/config'
import { createAdaptiveViewportMixin } from '@/mixins/adaptiveViewport'

export default {
  name: 'MaterialScanSummary',
  mixins: [createAdaptiveViewportMixin({ designWidth: 1600, designHeight: 980, minScale: 0.5, fitContentHeight: true })],
  data() {
    return {
      loading: false,
      groups: [],
      keyword: '',
      statusMessage: '',
      statusType: '',
      startDate: '',
      endDate: '',
      statCostThreshold: 100,
      roiThreshold: 1.5,
      selectedVideoKeys: [],
      videoMap: {},
      batchDownloading: false,
      batchDownloadIndex: 0,
      batchDownloadTotal: 0
    }
  },
  computed: {
    filteredGroups() {
      const keyword = String(this.keyword || '').trim().toLowerCase()
      const source = [...this.groups]
      source.sort((a, b) => {
        if (b.materialCount !== a.materialCount) {
          return b.materialCount - a.materialCount
        }
        return String(a.targetName || '').localeCompare(String(b.targetName || ''))
      })
      if (!keyword) {
        return source
      }
      return source.filter(group => {
        const targetName = String(group.targetName || '').toLowerCase()
        return targetName.includes(keyword)
      })
    },
    totalVideoCount() {
      return this.filteredGroups.reduce((sum, group) => sum + group.materialCount, 0)
    },
    selectedVideos() {
      return this.selectedVideoKeys
        .map(key => this.videoMap[key])
        .filter(Boolean)
    },
    allVideos() {
      return this.filteredGroups.flatMap(group => group.materials)
    },
    downloadProgressText() {
      if (!this.batchDownloadTotal) {
        return '0/0'
      }
      return `${this.batchDownloadIndex}/${this.batchDownloadTotal}`
    },
    downloadProgressPercent() {
      if (!this.batchDownloadTotal) {
        return 0
      }
      return Math.min(100, Math.round((this.batchDownloadIndex / this.batchDownloadTotal) * 100))
    }
  },
  created() {
    const defaultDate = this.getRelativeDate(-1)
    this.startDate = defaultDate
    this.endDate = defaultDate
    this.fetchSummary()
  },
  methods: {
    async fetchSummary() {
      this.loading = true
      this.statusMessage = ''
      try {
        const { startDate, endDate } = this.normalizeDateRange(this.startDate, this.endDate)
        const response = await axios.get(API_CONFIG.materialScanSummary(), {
          params: {
            startDate,
            endDate,
            statCostThreshold: this.toPositiveNumber(this.statCostThreshold, 100),
            roiThreshold: this.toPositiveNumber(this.roiThreshold, 1.5)
          }
        })
        const payload = response?.data?.data || response?.data || {}
        this.applySummaryData(payload)
        this.statusType = 'success'
        this.statusMessage = `加载完成，共 ${this.groups.length} 个 target_name 分组`
      } catch (error) {
        this.groups = []
        this.videoMap = {}
        this.selectedVideoKeys = []
        this.statusType = 'error'
        this.statusMessage = error.response?.data?.msg || error.response?.data?.message || '素材汇总加载失败'
      } finally {
        this.loading = false
      }
    },
    applySummaryData(payload) {
      const sourceGroups = Array.isArray(payload?.groups) ? payload.groups : []
      const nextVideoMap = {}
      const { startDate, endDate } = this.normalizeDateRange(payload?.startDate || this.startDate, payload?.endDate || this.endDate)
      const normalizedGroups = sourceGroups.map((group, groupIndex) => {
        const baseGroupKey = String(group?.groupKey || `${group?.jobId || groupIndex}_${group?.targetName || 'target'}`)
        const materials = Array.isArray(group?.materials)
          ? group.materials
          : this.flattenLegacyStatGroups(group?.statGroups)
        const normalizedMaterials = materials
          .map((material, materialIndex) => this.normalizeVideo(material, group, baseGroupKey, materialIndex))
          .filter(Boolean)
        normalizedMaterials.forEach((video) => {
          nextVideoMap[video.videoKey] = video
        })
        return {
          groupKey: baseGroupKey,
          targetName: group?.targetName || '',
          materials: normalizedMaterials,
          materialCount: normalizedMaterials.length
        }
      })

      this.groups = normalizedGroups
      this.startDate = startDate
      this.endDate = endDate
      this.videoMap = nextVideoMap
      this.selectedVideoKeys = this.selectedVideoKeys.filter(key => !!nextVideoMap[key])
    },
    flattenLegacyStatGroups(statGroups) {
      const source = Array.isArray(statGroups) ? statGroups : []
      return source.flatMap((statGroup) => {
        const videos = Array.isArray(statGroup?.video_materials) ? statGroup.video_materials : []
        return videos.map(video => ({
          material_id: video?.material_id ?? video?.materialId,
          url: video?.url,
          stat_cost_for_roi2: statGroup?.stat_cost_for_roi2,
          total_prepay_and_pay_settle_roi2_1h: statGroup?.total_prepay_and_pay_settle_roi2_1h
        }))
      })
    },
    normalizeVideo(video, group, baseGroupKey, materialIndex) {
      const materialId = video?.material_id ?? video?.materialId
      const url = this.normalizeMediaUrl(video?.url)
      if (!materialId || !url) {
        return null
      }
      return {
        videoKey: `${baseGroupKey}_${video?.stat_cost_for_roi2 || 0}_${video?.total_prepay_and_pay_settle_roi2_1h || 0}_${materialId}_${materialIndex}`,
        materialId,
        url,
        statCostForRoi2: this.toNumber(video?.stat_cost_for_roi2),
        totalPrepayAndPaySettleRoi21h: this.toNumber(video?.total_prepay_and_pay_settle_roi2_1h),
        targetName: group?.targetName || ''
      }
    },
    goBack() {
      this.$router.push('/data-overview')
    },
    getRelativeDate(offsetDays = 0) {
      const date = new Date()
      date.setDate(date.getDate() + offsetDays)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    normalizeDate(value, defaultValue) {
      const dateText = String(value || '').trim()
      return /^\d{4}-\d{2}-\d{2}$/.test(dateText) ? dateText : defaultValue
    },
    normalizeDateRange(startDate, endDate) {
      const defaultDate = this.getRelativeDate(-1)
      const normalizedStartDate = this.normalizeDate(startDate, defaultDate)
      const normalizedEndDate = this.normalizeDate(endDate, normalizedStartDate)
      return normalizedStartDate <= normalizedEndDate
        ? { startDate: normalizedStartDate, endDate: normalizedEndDate }
        : { startDate: normalizedEndDate, endDate: normalizedStartDate }
    },
    toNumber(value) {
      const num = Number(value)
      return Number.isFinite(num) ? num : 0
    },
    toPositiveNumber(value, defaultValue) {
      const num = Number(value)
      return Number.isFinite(num) && num > 0 ? num : defaultValue
    },
    formatNumber(value, fractionDigits = 2) {
      const numberValue = this.toNumber(value)
      return numberValue.toLocaleString('zh-CN', {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits
      })
    },
    normalizeMediaUrl(rawUrl) {
      return String(rawUrl || '')
        .replace(/[`'"]/g, '')
        .trim()
    },
    sanitizeFileName(name) {
      const baseName = String(name || 'video')
        .replace(/[\\/:*?"<>|]/g, '_')
        .replace(/\s+/g, ' ')
        .trim()
      return baseName || 'video'
    },
    isVideoSelected(videoKey) {
      return this.selectedVideoKeys.includes(videoKey)
    },
    toggleVideoSelection(videoKey) {
      if (!videoKey) {
        return
      }
      if (this.isVideoSelected(videoKey)) {
        this.selectedVideoKeys = this.selectedVideoKeys.filter(key => key !== videoKey)
      } else {
        this.selectedVideoKeys = [...this.selectedVideoKeys, videoKey]
      }
    },
    toggleTargetSelection(group) {
      const keys = group.materials.map(video => video.videoKey)
      this.toggleVideoKeyList(keys)
    },
    toggleVideoKeyList(keys) {
      const validKeys = Array.from(new Set((keys || []).filter(Boolean)))
      if (validKeys.length === 0) {
        return
      }
      const allSelected = validKeys.every(key => this.selectedVideoKeys.includes(key))
      if (allSelected) {
        this.selectedVideoKeys = this.selectedVideoKeys.filter(key => !validKeys.includes(key))
        return
      }
      this.selectedVideoKeys = Array.from(new Set([...this.selectedVideoKeys, ...validKeys]))
    },
    isTargetFullySelected(group) {
      const keys = group.materials.map(video => video.videoKey)
      return keys.length > 0 && keys.every(key => this.selectedVideoKeys.includes(key))
    },
    async triggerVideoDownload(url) {
      const directUrl = this.normalizeMediaUrl(url)
      if (!directUrl) {
        throw new Error('当前素材缺少可用的视频链接')
      }
      const link = document.createElement('a')
      link.href = directUrl
      link.target = '_blank'
      link.rel = 'noopener noreferrer'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
    async downloadSingleVideo(video) {
      try {
        await this.triggerVideoDownload(video.url)
        this.statusType = 'success'
        this.statusMessage = `已打开下载链接：${this.sanitizeFileName(`${video.targetName || 'video'}_${video.materialId}`)}`
      } catch (error) {
        this.statusType = 'error'
        this.statusMessage = error.message || '单个视频下载失败'
      }
    },
    async downloadSelectedVideos() {
      await this.downloadVideoList(this.selectedVideos, '已选素材')
    },
    async downloadAllVideos() {
      await this.downloadVideoList(this.allVideos, '全部素材')
    },
    async downloadGroupVideos(group) {
      await this.downloadVideoList(group.materials, `${group.targetName || '分组'}全部素材`)
    },
    async downloadVideoList(videoList, label) {
      const list = Array.isArray(videoList) ? videoList.filter(item => item && item.url) : []
      if (list.length === 0) {
        this.statusType = 'error'
        this.statusMessage = '没有可下载的视频素材'
        return
      }
      this.batchDownloading = true
      this.batchDownloadIndex = 0
      this.batchDownloadTotal = list.length
      this.statusType = 'success'
      this.statusMessage = `开始批量下载：${label}，共 ${list.length} 个视频`
      try {
        for (let i = 0; i < list.length; i += 1) {
          await this.triggerVideoDownload(list[i].url)
          this.batchDownloadIndex = i + 1
          await new Promise(resolve => setTimeout(resolve, 260))
        }
        this.statusType = 'success'
        this.statusMessage = `已依次打开 ${list.length} 个下载链接`
      } catch (error) {
        this.statusType = 'error'
        this.statusMessage = error.message || '批量下载过程中发生异常'
      } finally {
        this.batchDownloading = false
        this.batchDownloadIndex = 0
        this.batchDownloadTotal = 0
      }
    }
  }
}
</script>

<style scoped>
.material-summary-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #0f172a 0%, #1d4ed8 52%, #0f766e 100%);
  color: #f8fafc;
}

.page-header {
  position: sticky;
  top: 0;
  z-index: 10;
  backdrop-filter: blur(16px);
  background: rgba(15, 23, 42, 0.42);
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
}

.header-content,
.page-content {
  width: min(calc(100% - 32px), 1500px);
  margin: 0 auto;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 18px 0;
}

.back-button,
.ghost-button,
.primary-button {
  border: none;
  border-radius: 999px;
  cursor: pointer;
  transition: transform 0.2s ease, opacity 0.2s ease;
}

.back-button {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  color: #fff;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.22);
}

.back-button:hover,
.ghost-button:hover,
.primary-button:hover {
  transform: translateY(-1px);
}

.title-block {
  display: flex;
  align-items: center;
  gap: 14px;
}

.title-icon {
  width: 52px;
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.14);
}

.title-block h1 {
  margin: 0 0 4px;
  font-size: 28px;
}

.title-block p {
  margin: 0;
  color: rgba(248, 250, 252, 0.78);
  font-size: 14px;
}

.page-content {
  padding: 16px 0 24px;
}

.toolbar-card,
.summary-card,
.progress-card,
.target-card,
.empty-card {
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: rgba(255, 255, 255, 0.08);
  box-shadow: 0 18px 50px rgba(15, 23, 42, 0.18);
}

.toolbar-card {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 18px;
}

.toolbar-left {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.toolbar-title {
  font-size: 22px;
  font-weight: 700;
}

.toolbar-subtitle {
  color: rgba(248, 250, 252, 0.74);
  font-size: 14px;
}

.toolbar-right {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.toolbar-label {
  display: flex;
  align-items: center;
  gap: 8px;
  color: rgba(248, 250, 252, 0.88);
}

.toolbar-input {
  width: 220px;
  padding: 10px 14px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  outline: none;
}

.toolbar-date-input {
  width: 180px;
}

.toolbar-input::placeholder {
  color: rgba(248, 250, 252, 0.52);
}

.ghost-button {
  padding: 10px 16px;
  color: #fff;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.18);
}

.primary-button {
  padding: 10px 18px;
  color: #fff;
  background: linear-gradient(135deg, #38bdf8 0%, #6366f1 100%);
}

.ghost-button:disabled,
.primary-button:disabled {
  opacity: 0.65;
  cursor: not-allowed;
  transform: none;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
  margin-top: 12px;
}

.summary-card {
  border-radius: 14px;
  padding: 14px;
}

.summary-label {
  color: rgba(248, 250, 252, 0.74);
  font-size: 14px;
}

.summary-value {
  margin-top: 6px;
  font-size: 34px;
  font-weight: 800;
}

.status-banner {
  margin-top: 12px;
  padding: 12px 14px;
  border-radius: 14px;
  font-size: 14px;
}

.status-banner.success {
  background: rgba(34, 197, 94, 0.18);
  border: 1px solid rgba(34, 197, 94, 0.3);
  color: #dcfce7;
}

.status-banner.error {
  background: rgba(239, 68, 68, 0.18);
  border: 1px solid rgba(239, 68, 68, 0.28);
  color: #fee2e2;
}

.progress-card {
  margin-top: 12px;
  border-radius: 14px;
  padding: 12px 14px;
}

.progress-head {
  display: flex;
  justify-content: space-between;
  font-size: 14px;
  margin-bottom: 10px;
}

.progress-bar {
  height: 10px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.12);
  overflow: hidden;
}

.progress-bar-inner {
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg, #22c55e 0%, #38bdf8 100%);
}

.empty-card {
  margin-top: 14px;
  padding: 28px 16px;
  border-radius: 16px;
  text-align: center;
  color: rgba(248, 250, 252, 0.84);
}

.target-card {
  margin-top: 12px;
  border-radius: 18px;
  padding: 14px;
}

.target-card-header {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
}

.target-base {
  min-width: 0;
}

.target-title-row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.target-name {
  margin: 0;
  font-size: 28px;
}

.target-chip {
  padding: 6px 10px;
  border-radius: 999px;
  background: rgba(56, 189, 248, 0.18);
  color: #dbeafe;
  font-size: 12px;
}

.target-chip.soft {
  background: rgba(255, 255, 255, 0.1);
  color: rgba(248, 250, 252, 0.78);
}

.target-meta {
  margin-top: 6px;
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  color: rgba(248, 250, 252, 0.72);
  font-size: 13px;
}

.target-actions,
.stat-actions,
.video-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.stat-card {
  margin-top: 10px;
  border-radius: 16px;
  padding: 12px;
  background: rgba(15, 23, 42, 0.24);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.group-empty-card {
  margin-top: 10px;
  border-radius: 14px;
  padding: 18px 14px;
  text-align: center;
  color: rgba(248, 250, 252, 0.7);
  background: rgba(15, 23, 42, 0.18);
  border: 1px dashed rgba(255, 255, 255, 0.14);
}

.stat-card-header {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.stat-metrics {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.metric-pill {
  min-width: 132px;
  padding: 10px 12px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.08);
}

.metric-pill.roi {
  background: rgba(34, 197, 94, 0.12);
}

.metric-pill.count {
  background: rgba(99, 102, 241, 0.16);
}

.metric-pill-label {
  display: block;
  font-size: 12px;
  color: rgba(248, 250, 252, 0.68);
}

.metric-pill-value {
  display: block;
  margin-top: 4px;
  font-size: 20px;
  font-weight: 800;
}

.video-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
}

.video-card {
  border-radius: 14px;
  padding: 10px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.video-select {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  font-size: 12px;
  color: rgba(248, 250, 252, 0.84);
}

.video-preview {
  width: 100%;
  height: 220px;
  object-fit: cover;
  border-radius: 10px;
  background: rgba(15, 23, 42, 0.7);
}

.video-info {
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.video-info-row {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  font-size: 12px;
}

.video-label {
  color: rgba(248, 250, 252, 0.64);
}

.video-value {
  color: #fff;
  font-weight: 600;
  text-align: right;
}

.video-actions {
  margin-top: 8px;
}

@media (max-width: 1200px) {
  .summary-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .video-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 768px) {
  .header-content,
  .page-content {
    width: min(calc(100% - 20px), 1500px);
  }

  .header-content,
  .toolbar-card,
  .target-card-header,
  .stat-card-header {
    flex-direction: column;
    align-items: stretch;
  }

  .toolbar-input {
    width: 100%;
  }

  .summary-grid,
  .video-grid {
    grid-template-columns: 1fr;
  }

  .target-name {
    font-size: 22px;
  }

  .video-preview {
    height: 240px;
  }
}
</style>
